import { useState, useEffect, useContext } from 'react';
import { Box } from '@mui/material';
import { AppContext } from '../../../sporttia/all';
import { useCrud } from '../../../lib/hooks';
import ConsentDialog from '../../../components/dialogs/ConsentDialog';
import { SttConsentList } from '../../../sporttia/lists/SttConsentList';
import { useCanModifyConfiguration } from './useCanModifyConfiguration';

type Consent = {
    id?: number;
    name?: string;
    text?: string;
    textShort?: string;
    forBooking?: boolean;
};

type ConsentTemplateResponse = {
    consentTemplate: Consent;
};

export default function ConfigurationConsents(): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const canModify = useCanModifyConfiguration();
    const [consents, setConsents, , Get, Post, Put, Delete] = useCrud<
        Consent[]
    >([]);
    const [viewConsent, setViewConsent] = useState<Consent | null>(null);

    /**
     * Init
     */
    useEffect(() => {
        loadConsents();
    }, []);

    /**
     * Load constents
     */
    function loadConsents(params: { rows: number } = { rows: 20 }): void {
        Get(`/scs/${cxt?.sc?.id}/consents/templates`, {
            ...params,
        }).then((response: any) => {
            setConsents(response.rows);
        });
    }

    /**
     * Save constent
     */
    function saveConsent(consent: Consent): void {
        const { name, text, textShort, forBooking } = consent;
        if (consent.id) {
            Put(`/consents/templates/${consent.id}`, {
                name,
                text,
                textShort,
                forBooking,
            })
                .then((response: any) => {
                    const { consentTemplate: _consentTemplate } =
                        response as ConsentTemplateResponse;
                    setConsents(
                        consents?.map((csnt: any) =>
                            csnt.id === consent.id ? consent : csnt,
                        ) || [],
                    );
                })
                .finally(() => {
                    setViewConsent(null);
                });
        } else {
            Post(`/scs/${cxt?.sc?.id}/consents/templates`, {
                name,
                text,
                textShort,
                forBooking,
            })
                .then((response: any) => {
                    const { consentTemplate } =
                        response as ConsentTemplateResponse;
                    setConsents((consents || []).concat([consentTemplate]));
                })
                .finally(() => {
                    setViewConsent(null);
                });
        }
    }

    /**
     * Delete consent
     */
    function deleteConsent(consent: Consent): void {
        if (consent.id) {
            Delete(`/consents/templates/${consent.id}`)
                .then((response: any) => {
                    const { consentTemplate } =
                        response as ConsentTemplateResponse;
                    setConsents(
                        consents?.filter(
                            (csnt) => csnt.id !== consentTemplate.id,
                        ) || [],
                    );
                })
                .finally(() => {
                    setViewConsent(null);
                });
        }
    }

    // Render
    return (
        <Box display="flex" justifyContent="center">
            <Box flex={1}>
                <SttConsentList
                    consents={consents as any}
                    onAdd={
                        canModify
                            ? () =>
                                  setViewConsent({
                                      name: '',
                                      text: '',
                                      textShort: '',
                                      forBooking: false,
                                  })
                            : undefined
                    }
                    onSelect={(consent: any) =>
                        setViewConsent(consent as Consent)
                    }
                    onDelete={
                        canModify
                            ? (consent: any) =>
                                  deleteConsent(consent as Consent)
                            : undefined
                    }
                />
            </Box>
            <ConsentDialog
                item={viewConsent}
                onClose={() => setViewConsent(null)}
                onSave={
                    canModify
                        ? (consent: any) => saveConsent(consent as Consent)
                        : undefined
                }
            />
        </Box>
    );
}
